/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-09-17 12:30:39
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import { prettyText } from './index';
import {
  DEFAULT_SUGGESTION_BLOCK,
  DEFAULT_LAYOUT_SETTINGS
} from 'src/constants/instant-search-display';
import { checkHasSpecialCharacters } from 'src/helpers';

import styleDefault from 'src/assets/images/instant-search-widget/style1-grid.png';
import style1Grid from 'src/assets/images/instant-search-widget/style1-grid.png';
import style1List from 'src/assets/images/instant-search-widget/style1-list.png';
import style2GridLeft from 'src/assets/images/instant-search-widget/style2-grid-left.png';
import style2GridRight from 'src/assets/images/instant-search-widget/style2-grid-right.png';
import style2ListLeft1 from 'src/assets/images/instant-search-widget/style2-list-left-1.png';
import style2ListLeft2 from 'src/assets/images/instant-search-widget/style2-list-left-2.png';
import style2ListRight1 from 'src/assets/images/instant-search-widget/style2-list-right-1.png';
import style2ListRight2 from 'src/assets/images/instant-search-widget/style2-list-right-2.png';
import style3GridLeft from 'src/assets/images/instant-search-widget/style3-grid-left.png';
import style3GridRight from 'src/assets/images/instant-search-widget/style3-grid-right.png';
import style3ListLeft2 from 'src/assets/images/instant-search-widget/style3-list-left-2.png';
import style3ListLeft3 from 'src/assets/images/instant-search-widget/style3-list-left-3.png';
import style3ListRight2 from 'src/assets/images/instant-search-widget/style3-list-right-2.png';
import style3ListRight3 from 'src/assets/images/instant-search-widget/style3-list-right-3.png';

/**
 * Build initial data for instant search display
 *
 * @param  {Object} filterSettings filter settings from API
 * @param {i18n} I18n
 *
 * @return {Object} initialValue
 */
export const buildInitialInstantSearchDisplayValues = (
  filterSettings: Object,
  themeSetupV2: boolean,
  i18n: Object
) => {
  const formattedDefaultSuggestionBlock = DEFAULT_SUGGESTION_BLOCK.map(function (block) {
    return {
      ...block,
      label: i18n.translate(block.label)
    };
  });
  let suggestionBlocksAPI = _.get(
    filterSettings,
    'search.suggestionBlocks',
    formattedDefaultSuggestionBlock
  );

  // Set default values if suggestion blocks is empty
  if (_.isEmpty(suggestionBlocksAPI)) {
    suggestionBlocksAPI = formattedDefaultSuggestionBlock;
  }

  const suggestionBlocks = _.map(suggestionBlocksAPI, item => {
    return {
      ...item,
      number: item.number ? item.number.toString() : ''
    };
  });

  // Hotfix: Reverse productAvailable
  let productAvailable = _.get(filterSettings, 'search.productAvailable', false);
  productAvailable = !productAvailable;

  // Initial label for recent search, search term and product suggestion label
  // Search box onclick
  let searchBoxRecentSearchLabel = _.get(
    filterSettings,
    'search.searchBoxOnclick.recentSearch.label',
    ''
  );
  searchBoxRecentSearchLabel = !_.isEmpty(searchBoxRecentSearchLabel)
    ? searchBoxRecentSearchLabel
    : i18n.translate('AdminHTML.InstantSearchDisplay.Helper.searchBoxRecentSearchLabelDefault');

  let searchBoxSearchTermSuggestionsLabel = _.get(
    filterSettings,
    'search.searchBoxOnclick.searchTermSuggestion.label',
    ''
  );
  searchBoxSearchTermSuggestionsLabel = !_.isEmpty(searchBoxSearchTermSuggestionsLabel)
    ? searchBoxSearchTermSuggestionsLabel
    : i18n.translate(
        'AdminHTML.InstantSearchDisplay.Helper.searchBoxSearchTermSuggestionsLabelDefault'
      );

  let searchBoxProductSuggestionsLabel = _.get(
    filterSettings,
    'search.searchBoxOnclick.productSuggestion.label',
    ''
  );
  searchBoxProductSuggestionsLabel = !_.isEmpty(searchBoxProductSuggestionsLabel)
    ? searchBoxProductSuggestionsLabel
    : i18n.translate(
        'AdminHTML.InstantSearchDisplay.Helper.searchBoxProductSuggestionsLabelDefault'
      );

  // Initial label for search term and product suggestion label
  // Suggestion no result
  let searchTermSuggestionsLabel = _.get(
    filterSettings,
    'search.suggestionNoResult.search_terms.label',
    ''
  );
  const searchTermSuggestionsType = _.get(
    filterSettings,
    'search.suggestionNoResult.search_terms.type',
    'based_on_data'
  );
  searchTermSuggestionsLabel = !_.isEmpty(searchTermSuggestionsLabel)
    ? searchTermSuggestionsLabel
    : i18n.translate('AdminHTML.InstantSearchDisplay.Helper.searchTermSuggestionsLabelDefault');

  let productSuggestionsLabel = _.get(
    filterSettings,
    'search.suggestionNoResult.products.label',
    ''
  );
  productSuggestionsLabel = !_.isEmpty(productSuggestionsLabel)
    ? productSuggestionsLabel
    : i18n.translate('AdminHTML.InstantSearchDisplay.Helper.productSuggestionsDefault');

  const productSuggestionsType = _.get(
    filterSettings,
    'search.suggestionNoResult.products.type',
    'based_on_data'
  );

  const scopedSuggestions = _.get(filterSettings, 'search.scopedSuggestions', {
    applyScopedSuggestion: false
  });
  if (scopedSuggestions.scopedSuggestionFields) {
    const fields = scopedSuggestions.scopedSuggestionFields.split(',');
    scopedSuggestions.option1 = fields[0];
    scopedSuggestions.option2 = fields[1];
  } else {
    scopedSuggestions.option1 = 'collection';
    scopedSuggestions.option2 = 'vendor';
  }

  let result = {
    enableSuggestion: _.get(filterSettings, 'search.enableSuggestion', false),
    productAvailable,
    showSuggestionProductImage: _.get(filterSettings, 'search.showSuggestionProductImage', false),
    showSuggestionProductPrice: _.get(filterSettings, 'search.showSuggestionProductPrice', false),
    showSuggestionProductSalePrice: _.get(
      filterSettings,
      'search.showSuggestionProductSalePrice',
      false
    ),
    showSuggestionProductSku: _.get(filterSettings, 'search.showSuggestionProductSku', false),
    showSuggestionProductVendor: _.get(filterSettings, 'search.showSuggestionProductVendor', false),
    suggestionBlocks,
    // Search box onclick
    enableSearchBoxRecentSearch: _.get(
      filterSettings,
      'search.searchBoxOnclick.recentSearch.status',
      true
    ),
    searchBoxRecentSearchLabel,
    searchBoxRecentSearchNumber: _.get(
      filterSettings,
      'search.searchBoxOnclick.recentSearch.number',
      '3'
    ),
    enableSearchBoxSearchTermSuggestions: _.get(
      filterSettings,
      'search.searchBoxOnclick.searchTermSuggestion.status',
      false
    ),
    searchBoxSearchTermSuggestionsLabel,
    searchBoxSearchTermSuggestionsList: _.get(
      filterSettings,
      'search.searchBoxOnclick.searchTermSuggestion.data',
      []
    ),
    searchBoxSearchTermSuggestionsBackup: _.get(
      filterSettings,
      'search.searchBoxOnclick.searchTermSuggestion.backup',
      []
    ),
    searchBoxSearchTermSuggestionsType: _.get(
      filterSettings,
      'search.searchBoxOnclick.searchTermSuggestion.type',
      'manually'
    ),
    enableSearchBoxProductSuggestions: _.get(
      filterSettings,
      'search.searchBoxOnclick.productSuggestion.status',
      false
    ),
    searchBoxProductSuggestionsLabel,
    searchBoxProductSuggestionsList: _.get(
      filterSettings,
      'search.searchBoxOnclick.productSuggestion.data',
      []
    ),
    searchBoxProductSuggestionsType: _.get(
      filterSettings,
      'search.searchBoxOnclick.productSuggestion.type',
      'manually'
    ),
    searchBoxProductSuggestionsBackup: _.get(
      filterSettings,
      'search.searchBoxOnclick.productSuggestion.backup',
      []
    ),
    // Suggestion no result
    enableSearchTermSuggestions: _.get(
      filterSettings,
      'search.suggestionNoResult.search_terms.status',
      false
    ),
    searchTermSuggestionsLabel,
    searchTermSuggestionsList: _.get(
      filterSettings,
      'search.suggestionNoResult.search_terms.data',
      []
    ),
    searchTermSuggestionsBackup: _.get(
      filterSettings,
      'search.suggestionNoResult.search_terms.backup',
      []
    ),
    searchTermSuggestionsType,
    enableProductSuggestions: _.get(
      filterSettings,
      'search.suggestionNoResult.products.status',
      false
    ),
    productSuggestionsLabel,
    productSuggestionsList: _.get(filterSettings, 'search.suggestionNoResult.products.data', []),
    productSuggestionsBackup: _.get(
      filterSettings,
      'search.suggestionNoResult.products.backup',
      []
    ),
    productSuggestionsType,
    scopedSuggestions
  };

  // Layout settings
  if (themeSetupV2) {
    let suggestionStyle2ProductPerRow = _.get(
      filterSettings,
      'search.suggestionStyle2ProductPerRow',
      DEFAULT_LAYOUT_SETTINGS.suggestionStyle2ProductPerRow
    );
    if (!_.isEmpty(suggestionStyle2ProductPerRow)) {
      suggestionStyle2ProductPerRow = suggestionStyle2ProductPerRow.toString();
    }

    let suggestionStyle3ProductPerRow = _.get(
      filterSettings,
      'search.suggestionStyle3ProductPerRow',
      DEFAULT_LAYOUT_SETTINGS.suggestionStyle3ProductPerRow
    );
    if (!_.isEmpty(suggestionStyle3ProductPerRow)) {
      suggestionStyle3ProductPerRow = suggestionStyle3ProductPerRow.toString();
    }

    const layoutSettings = {
      suggestionStyle: _.get(
        filterSettings,
        'search.suggestionStyle',
        DEFAULT_LAYOUT_SETTINGS.suggestionStyle
      ),
      suggestionStyle1ProductItemType: _.get(
        filterSettings,
        'search.suggestionStyle1ProductItemType',
        DEFAULT_LAYOUT_SETTINGS.suggestionStyle1ProductItemType
      ),
      suggestionStyle2ProductItemType: _.get(
        filterSettings,
        'search.suggestionStyle2ProductItemType',
        DEFAULT_LAYOUT_SETTINGS.suggestionStyle2ProductItemType
      ),
      suggestionStyle2ProductPosition: _.get(
        filterSettings,
        'search.suggestionStyle2ProductPosition',
        DEFAULT_LAYOUT_SETTINGS.suggestionStyle2ProductPosition
      ),
      suggestionStyle2ProductPerRow,
      suggestionStyle3ProductItemType: _.get(
        filterSettings,
        'search.suggestionStyle3ProductItemType',
        DEFAULT_LAYOUT_SETTINGS.suggestionStyle3ProductItemType
      ),
      suggestionStyle3ProductPosition: _.get(
        filterSettings,
        'search.suggestionStyle3ProductPosition',
        DEFAULT_LAYOUT_SETTINGS.suggestionStyle3ProductPosition
      ),
      suggestionStyle3ProductPerRow
    };

    result = { ...result, ...layoutSettings };
  }

  return result;
};

/**
 * Check validate instant search display
 *
 * @param  {array} data
 * @param {i18n} I18n
 *
 * @return {string}          value
 */
export const checkValidateInstantSearchDisplay = (data: Object, i18n: Object) => {
  const {
    suggestionBlocks,
    enableSearchBoxRecentSearch,
    searchBoxRecentSearchLabel,
    enableSearchBoxSearchTermSuggestions,
    searchBoxSearchTermSuggestionsLabel,
    enableSearchBoxProductSuggestions,
    searchBoxProductSuggestionsLabel,
    productSuggestionsLabel,
    searchTermSuggestionsLabel,
    enableSearchTermSuggestions,
    enableProductSuggestions
  } = data;
  for (let item of suggestionBlocks) {
    if (item.label.trim().length > 128) {
      return i18n.translate(
        'AdminHTML.InstantSearchDisplay.Helper.instantSearchDisplayHelper_CheckValidateInstantSearchDisplay_Label1'
      );
    }
    if (_.isEmpty(item.label.trim()) || checkHasSpecialCharacters(item.label, '&')) {
      return i18n.translate(
        'AdminHTML.InstantSearchDisplay.Helper.instantSearchDisplayHelper_CheckValidateInstantSearchDisplay_Label2'
      );
    }
  }

  if (searchBoxRecentSearchLabel.trim().length > 128) {
    return i18n.translate(
      'AdminHTML.InstantSearchDisplay.Helper.invalid_searchBoxRecentSearchLabel_Length'
    );
  }

  if (
    (_.isEmpty(searchBoxRecentSearchLabel.trim()) && enableSearchBoxRecentSearch) ||
    checkHasSpecialCharacters(searchBoxRecentSearchLabel)
  ) {
    return i18n.translate(
      'AdminHTML.InstantSearchDisplay.Helper.invalid_searchBoxRecentSearchLabel_SpecialCharacter'
    );
  }

  if (searchBoxSearchTermSuggestionsLabel.trim().length > 128) {
    return i18n.translate(
      'AdminHTML.InstantSearchDisplay.Helper.invalid_searchBoxSearchTermSuggestionsLabel_Length'
    );
  }

  if (
    (_.isEmpty(searchBoxSearchTermSuggestionsLabel.trim()) &&
      enableSearchBoxSearchTermSuggestions) ||
    checkHasSpecialCharacters(searchBoxSearchTermSuggestionsLabel)
  ) {
    return i18n.translate(
      'AdminHTML.InstantSearchDisplay.Helper.invalid_searchBoxSearchTermSuggestionsLabel_SpecialCharacter'
    );
  }

  if (searchBoxProductSuggestionsLabel.trim().length > 128) {
    return i18n.translate(
      'AdminHTML.InstantSearchDisplay.Helper.invalid_searchBoxProductSuggestionsLabel_Length'
    );
  }

  if (
    (_.isEmpty(searchBoxProductSuggestionsLabel.trim()) && enableSearchBoxProductSuggestions) ||
    checkHasSpecialCharacters(searchBoxProductSuggestionsLabel)
  ) {
    return i18n.translate(
      'AdminHTML.InstantSearchDisplay.Helper.invalid_searchBoxProductSuggestionsLabel_SpecialCharacter'
    );
  }

  if (productSuggestionsLabel.trim().length > 128) {
    return i18n.translate(
      'AdminHTML.InstantSearchDisplay.Helper.instantSearchDisplayHelper_CheckValidateInstantSearchDisplay_Label3'
    );
  }

  if (
    (_.isEmpty(productSuggestionsLabel.trim()) && enableProductSuggestions) ||
    checkHasSpecialCharacters(productSuggestionsLabel)
  ) {
    return i18n.translate(
      'AdminHTML.InstantSearchDisplay.Helper.instantSearchDisplayHelper_CheckValidateInstantSearchDisplay_Label4'
    );
  }

  if (searchTermSuggestionsLabel.trim().length > 128) {
    return i18n.translate(
      'AdminHTML.InstantSearchDisplay.Helper.instantSearchDisplayHelper_CheckValidateInstantSearchDisplay_Label5'
    );
  }

  if (
    (_.isEmpty(searchTermSuggestionsLabel.trim()) && enableSearchTermSuggestions) ||
    checkHasSpecialCharacters(searchTermSuggestionsLabel)
  ) {
    return i18n.translate(
      'AdminHTML.InstantSearchDisplay.Helper.instantSearchDisplayHelper_CheckValidateInstantSearchDisplay_Label6'
    );
  }
  return '';
};

/**
 * Build data for saving filter settings
 *
 * @param  {Object} data instant search display data
 * @param  {Object} filterSettings filter settings from API
 * @param  {boolean} notThemeSetupV1 flag notThemeSetupV1 from store
 * @param  {string} primaryLocale primary locale of store,
 *
 * @return {Object} value value for sending API
 */
export const buildInstantSearchDisplayValues = (
  data: Object,
  filterSettings: Object,
  notThemeSetupV1: boolean,
  primaryLocale: string
) => {
  const { suggestionBlocks: suggestionBlocksForm } = data;

  const suggestionBlocks = suggestionBlocksForm.map(item => {
    return {
      ...item,
      number: item.number ? parseInt(item.number) : 5,
      label: prettyText(item.label.toString())
    };
  });

  // Hotfix: Reverse product available
  let productAvailable = _.get(data, 'productAvailable', false);
  productAvailable = !productAvailable;

  // Search box onclick
  const searchBoxOnclick = buildSearchBoxOnclick(data);

  // Suggestion no result
  const suggestionNoResult = buildSuggestionNoResult(data);

  const scopedSuggestions = buildScopedSuggestions(data);

  // Convert data
  let search = {
    enableSuggestion: _.get(data, 'enableSuggestion', false),
    productAvailable,
    showSuggestionProductImage: _.get(data, 'showSuggestionProductImage', false),
    showSuggestionProductPrice: _.get(data, 'showSuggestionProductPrice', false),
    showSuggestionProductSalePrice: _.get(data, 'showSuggestionProductSalePrice', false),
    showSuggestionProductSku: _.get(data, 'showSuggestionProductSku', false),
    showSuggestionProductVendor: _.get(data, 'showSuggestionProductVendor', false),
    suggestionBlocks,
    searchBoxOnclick,
    suggestionNoResult,
    scopedSuggestions
  };

  if (notThemeSetupV1) {
    const layoutSettings = buildLayoutSettings(data);
    search = { ...search, ...layoutSettings };
  }

  // Save labels as translation values (only apply to primary locale)
  let updatedFilterSettings = saveSuggestionBlocksToTranslation(
    suggestionBlocks,
    filterSettings,
    primaryLocale
  );

  updatedFilterSettings = saveSearchBoxOnclickToTranslation(
    searchBoxOnclick,
    updatedFilterSettings,
    primaryLocale
  );

  updatedFilterSettings = saveSuggestionNoResultToTranslation(
    suggestionNoResult,
    updatedFilterSettings,
    primaryLocale
  );

  const newSettings = {
    ...updatedFilterSettings,
    search: { ...updatedFilterSettings.search, ...search }
  };

  return newSettings;
};

/**
 * Build Search box onclick
 * @param {*} data
 * @returns
 */
const buildSearchBoxOnclick = (data: Object) => {
  const { searchBoxProductSuggestionsList: searchBoxProductSuggestionsListForm = [] } = data;

  const searchBoxOnclick = {
    recentSearch: {
      label: prettyText(_.get(data, 'searchBoxRecentSearchLabel', '')),
      status: _.get(data, 'enableSearchBoxRecentSearch', true),
      number: _.get(data, 'searchBoxRecentSearchNumber', '3')
    },
    searchTermSuggestion: {
      label: prettyText(_.get(data, 'searchBoxSearchTermSuggestionsLabel', '')),
      status: _.get(data, 'enableSearchBoxSearchTermSuggestions', false),
      data: _.get(data, 'searchBoxSearchTermSuggestionsList', []),
      backup: _.get(data, 'searchBoxSearchTermSuggestionsBackup', []),
      type: _.get(data, 'searchBoxSearchTermSuggestionsType', 'manually')
    },
    productSuggestion: {
      label: prettyText(_.get(data, 'searchBoxProductSuggestionsLabel', '')),
      status: _.get(data, 'enableSearchBoxProductSuggestions', false),
      data: _.get(data, 'searchBoxProductSuggestionsList', []),
      backup: _.get(data, 'searchBoxProductSuggestionsBackup', []),
      type: _.get(data, 'searchBoxProductSuggestionsType', 'manually')
    }
  };

  // Build mapping variants if suggestion product has id who is variant id
  // => build field variants
  const variants = [];
  searchBoxProductSuggestionsListForm.forEach(product => {
    if (product.product_id && product.id !== product.product_id) {
      variants.push({ productId: product.product_id, variantId: product.id });
    }
  });

  // Create mapping field. It is used to switch back to original product if turn off Variants display
  if (variants.length > 0) {
    searchBoxOnclick.productSuggestion.variants = variants;
  }

  return searchBoxOnclick;
};

/**
 * Build suggestion no result
 * @param {*} data
 * @param {*} filterSettings
 * @returns
 */
const buildSuggestionNoResult = (data: Object) => {
  const {
    searchTermSuggestionsLabel = '',
    productSuggestionsLabel = '',
    enableSearchTermSuggestions = false,
    enableProductSuggestions = false,
    searchTermSuggestionsList = [],
    searchTermSuggestionsBackup = [],
    productSuggestionsList = [],
    productSuggestionsBackup = [],
    searchTermSuggestionsType = 'based_on_data',
    productSuggestionsType = 'based_on_data'
  } = data;

  const suggestionNoResult = {
    search_terms: {
      label: prettyText(searchTermSuggestionsLabel),
      status: enableSearchTermSuggestions,
      data: searchTermSuggestionsList,
      type: searchTermSuggestionsType,
      backup: searchTermSuggestionsBackup
    },
    products: {
      label: prettyText(productSuggestionsLabel),
      status: enableProductSuggestions,
      data: productSuggestionsList,
      type: productSuggestionsType,
      backup: productSuggestionsBackup
    }
  };

  // Build mapping variants if suggestion product has id who is variant id
  // => build field variants
  const noSearchResultVariants = [];
  productSuggestionsList.forEach(product => {
    if (product.product_id && product.id !== product.product_id) {
      noSearchResultVariants.push({ productId: product.product_id, variantId: product.id });
    }
  });

  // Create mapping field. It is used to switch back to original product if turn off Variants display
  if (noSearchResultVariants.length > 0) {
    suggestionNoResult.products.variants = noSearchResultVariants;
  }

  return suggestionNoResult;
};

/**
 * Build layout settings
 * @param {*} data
 * @returns
 */
const buildLayoutSettings = (data: Object) => {
  const { suggestionStyle1ProductItemType = 'grid' } = data;

  return {
    suggestionStyle: _.get(data, 'suggestionStyle', DEFAULT_LAYOUT_SETTINGS.suggestionStyle),
    suggestionStyle1ProductItemType: _.get(
      data,
      'suggestionStyle1ProductItemType',
      DEFAULT_LAYOUT_SETTINGS.suggestionStyle1ProductItemType
    ),
    suggestionStyle1ProductPosition: DEFAULT_LAYOUT_SETTINGS.suggestionStyle1ProductPosition,
    suggestionStyle1ProductPerRow: suggestionStyle1ProductItemType === 'grid' ? '3' : '1',
    suggestionStyle2ProductItemType: _.get(
      data,
      'suggestionStyle2ProductItemType',
      DEFAULT_LAYOUT_SETTINGS.suggestionStyle2ProductItemType
    ),
    suggestionStyle2ProductPosition: _.get(
      data,
      'suggestionStyle2ProductPosition',
      DEFAULT_LAYOUT_SETTINGS.suggestionStyle2ProductPosition
    ),
    suggestionStyle2ProductPerRow: _.get(
      data,
      'suggestionStyle2ProductPerRow',
      DEFAULT_LAYOUT_SETTINGS.suggestionStyle2ProductPerRow
    ),
    suggestionStyle3ProductItemType: _.get(
      data,
      'suggestionStyle3ProductItemType',
      DEFAULT_LAYOUT_SETTINGS.suggestionStyle3ProductItemType
    ),
    suggestionStyle3ProductPosition: _.get(
      data,
      'suggestionStyle3ProductPosition',
      DEFAULT_LAYOUT_SETTINGS.suggestionStyle3ProductPosition
    ),
    suggestionStyle3ProductPerRow: _.get(
      data,
      'suggestionStyle3ProductPerRow',
      DEFAULT_LAYOUT_SETTINGS.suggestionStyle3ProductPerRow
    )
  };
};

/**
 * Save suggestion label to translation values as primary locale
 *
 * @param  {object} suggestionBlocks
 * @param  {object} filterSettings
 * @param  {string} primaryLocale
 *
 * @return {object}                  updated filter settings
 */
const saveSuggestionBlocksToTranslation = (
  suggestionBlocks: Object,
  filterSettings: Object,
  primaryLocale: string
) => {
  let { label = {}, labelTranslations = {} } = filterSettings;

  const types = {
    suggestions: 'instantSearchSuggestionsLabel',
    collections: 'instantSearchCollectionsLabel',
    products: 'instantSearchProductsLabel',
    pages: 'instantSearchPagesLabel'
  };

  // Update translation label
  suggestionBlocks.map(block => {
    const { type, label: suggestionLabel } = block;
    const key = types[type];

    _.set(label, key, suggestionLabel);
    _.set(labelTranslations, `${primaryLocale}.${key}`, suggestionLabel);

    return suggestionLabel;
  });

  return {
    ...filterSettings,
    label,
    labelTranslations
  };
};

/**
 * Save search box onclick label to translation values as primary locale
 *
 * @param  {object} searchBoxOnclick
 * @param  {object} filterSettings
 * @param  {string} primaryLocale
 *
 * @return {object}                  updated filter settings
 */
const saveSearchBoxOnclickToTranslation = (searchBoxOnclick, filterSettings, primaryLocale) => {
  let { label = {}, labelTranslations = {} } = filterSettings;

  const types = {
    productSuggestion: 'searchBoxOnclickProductsLabel',
    recentSearch: 'searchBoxOnclickRecentSearchLabel',
    searchTermSuggestion: 'searchBoxOnclickSearchTermLabel'
  };

  // Update translation label
  for (let key in searchBoxOnclick) {
    // skip loop if the property is from prototype
    if (!searchBoxOnclick.hasOwnProperty(key)) continue;

    const item = searchBoxOnclick[key];
    const { label: searchBoxOnclickLabel } = item;
    const type = types[key];
    if (!_.isEmpty(type)) {
      _.set(label, type, searchBoxOnclickLabel);
      _.set(labelTranslations, `${primaryLocale}.${type}`, searchBoxOnclickLabel);
    }
  }

  return {
    ...filterSettings,
    label,
    labelTranslations
  };
};

/**
 * Save suggestion no result label to translation values as primary locale
 *
 * @param  {object} suggestionNoResult
 * @param  {object} filterSettings
 * @param  {string} primaryLocale
 *
 * @return {object}                  updated filter settings
 */
const saveSuggestionNoResultToTranslation = (suggestionNoResult, filterSettings, primaryLocale) => {
  let { label = {}, labelTranslations = {} } = filterSettings;

  const types = {
    products: 'noSearchResultProductsLabel',
    search_terms: 'noSearchResultSearchTermLabel'
  };

  // Update translation label
  for (let key in suggestionNoResult) {
    // skip loop if the property is from prototype
    if (!suggestionNoResult.hasOwnProperty(key)) continue;

    const item = suggestionNoResult[key];
    const { label: suggestionNoResultLabel } = item;
    const type = types[key];
    if (!_.isEmpty(type)) {
      _.set(label, type, suggestionNoResultLabel);
      _.set(labelTranslations, `${primaryLocale}.${type}`, suggestionNoResultLabel);
    }
  }

  return {
    ...filterSettings,
    label,
    labelTranslations
  };
};

/**
 * Check validate search term suggestion
 *
 * @param  {string} searchTermSuggestion
 * @param  {array} searchTermSuggestionsList
 * @param  {I18n} i18n
 *
 * @return {Object} checked result
 *
 */
export const checkValidateSearchTermSuggestion = (
  searchTermSuggestion: Object,
  searchTermSuggestionsList: Array<Object>,
  i18n: Object
) => {
  let value = searchTermSuggestion.trim().toLowerCase();

  if (value) {
    if (value.length > 128) {
      return {
        error: i18n.translate(
          'AdminHTML.InstantSearchDisplay.Helper.instantSearchDisplayHelper_CheckValidateSearchTermSuggestion_Error1'
        )
      };
    }

    let checkSpecialCharactersValue = checkHasSpecialCharacters(value);
    if (_.isEmpty(value) || checkSpecialCharactersValue) {
      return {
        error: i18n.translate(
          'AdminHTML.InstantSearchDisplay.Helper.instantSearchDisplayHelper_CheckValidateSearchTermSuggestion_Error2'
        )
      };
    }

    const found = _.find(searchTermSuggestionsList, item => item === value);
    if (_.isEmpty(found)) {
      return {
        searchTermSuggestion: value,
        error: ''
      };
    } else {
      return {
        error: i18n.translate(
          'AdminHTML.InstantSearchDisplay.Helper.instantSearchDisplayHelper_CheckValidateSearchTermSuggestion_Error3'
        )
      };
    }
  }

  return {};
};

/**
 * Get the layout settings image
 */
export const getLayoutSettingsImage = (
  style: string,
  type: string = 'list',
  position: string = 'right',
  productPerRow: string = '1'
) => {
  switch (style) {
    case 'style1': {
      switch (type) {
        case 'grid':
          return style1Grid;
        case 'list':
          return style1List;
        default:
          return styleDefault;
      }
    }
    case 'style2': {
      switch (type) {
        case 'grid': {
          switch (position) {
            case 'left':
              return style2GridLeft;
            case 'right':
              return style2GridRight;
            default:
              return styleDefault;
          }
        }
        case 'list': {
          switch (position) {
            case 'left': {
              switch (productPerRow) {
                case '1':
                  return style2ListLeft1;
                case '2':
                  return style2ListLeft2;
                default:
                  return styleDefault;
              }
            }
            case 'right':
              switch (productPerRow) {
                case '1':
                  return style2ListRight1;
                case '2':
                  return style2ListRight2;
                default:
                  return styleDefault;
              }
            default:
              return styleDefault;
          }
        }
        default:
          return styleDefault;
      }
    }
    case 'style3': {
      switch (type) {
        case 'grid': {
          switch (position) {
            case 'left':
              return style3GridLeft;
            case 'right':
              return style3GridRight;
            default:
              return styleDefault;
          }
        }
        case 'list': {
          switch (position) {
            case 'left': {
              switch (productPerRow) {
                case '2':
                  return style3ListLeft2;
                case '3':
                  return style3ListLeft3;
                default:
                  return styleDefault;
              }
            }
            case 'right':
              switch (productPerRow) {
                case '2':
                  return style3ListRight2;
                case '3':
                  return style3ListRight3;
                default:
                  return styleDefault;
              }
            default:
              return styleDefault;
          }
        }
        default:
          return styleDefault;
      }
    }
    default:
      return styleDefault;
  }
};

const buildScopedSuggestions = data => {
  const { scopedSuggestions } = data;
  if (scopedSuggestions && scopedSuggestions.applyScopedSuggestion) {
    return {
      applyScopedSuggestion: true,
      scopedSuggestionFields: `${scopedSuggestions.option1},${scopedSuggestions.option2}`
    };
  }

  return {
    applyScopedSuggestion: false
  };
};
